import React, { useCallback, useEffect, useState } from "react";
import { FilePlus2 } from "lucide-react";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

const money = (c) => `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ChangeOrderPanel({ pid }) {
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase3/change-orders/projects/${pid}`).then((r) => setData(r.data)).catch(() => setData(null)), [pid]);
  useEffect(() => { load(); }, [load]);
  const run = (p) => p.then(() => { setError(null); load(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  if (!data) return null;
  const founder = data.role_key === "OWNER_SUPER_ADMIN";
  return (
    <Card className="ds-card border-erf-borderCream mt-6" data-testid="phase3-co-panel">
      <CardContent className="p-5 space-y-3 text-sm font-nav text-erf-dark">
        <h2 className="font-display font-semibold text-base md:text-lg">Change orders</h2>
        <p className="text-xs text-erf-dark/80" data-testid="phase3-co-threshold">Change orders of {money(data.founder_threshold_cents)} or more need the Founder. Approved change orders are added to the billing schedule automatically.</p>
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); run(api.post(`/phase3/change-orders/projects/${pid}`, { title, amount_cents: Math.round(Number(amount) * 100) }).then(() => { setTitle(""); setAmount(""); })); }}>
          <label>Title<Input required value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 h-11" data-testid="phase3-co-title" /></label>
          <label>Amount ($)<Input required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-11" data-testid="phase3-co-amount" /></label>
          <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid="phase3-co-create"><FilePlus2 className="w-4 h-4 mr-1.5" aria-hidden="true" />New change order</Button>
        </form>
        <label className="block">Reason for rejecting (at least 10 characters)<Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-11" data-testid="phase3-co-reason" /></label>
        {error && <p role="alert" className="text-sm text-[#991B1B]" data-testid="phase3-co-error">{error}</p>}
        <ul className="space-y-2">
          {data.items.length === 0 && <li>No change orders yet.</li>}
          {data.items.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2" data-testid={`phase3-co-${c.number}`}>
              <span className="mr-auto"><strong>{c.number}</strong> {c.title} · {money(c.amount_cents)} · {c.state}{c.sov_line_id ? " · on billing schedule" : ""}</span>
              {c.state === "draft" && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase3/change-orders/${c.id}/submit`))} data-testid={`phase3-co-submit-${c.number}`}>Submit</Button>}
              {c.state === "submitted" && (founder || c.amount_cents < data.founder_threshold_cents) && data.role_key !== "PROJECT_MANAGER" && (
                <>
                  <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase3/change-orders/${c.id}/approve`))} data-testid={`phase3-co-approve-${c.number}`}>Approve</Button>
                  <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase3/change-orders/${c.id}/reject`, { reason }))} data-testid={`phase3-co-reject-${c.number}`}>Reject</Button>
                </>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
